import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DownloadIcon,
  FileIcon,
  FileTextIcon,
  ImageIcon,
  LinkIcon,
  PencilIcon,
  StarIcon,
  Trash2Icon,
  UploadIcon,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { LoadingRows } from '@/components/skeletons'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

import {
  attachmentTypesKey,
  attachmentsKey,
  deleteAttachment,
  fetchAttachmentTypes,
  fileUrl,
  formatBytes,
  guessTypeId,
  importAttachmentUrl,
  isImage,
  isPdf,
  patchAttachment,
  thumbUrl,
  uploadAttachment,
  useAttachments,
  type AttachmentView,
  type OwnerType,
} from './api'
import { AttachmentViewer } from './AttachmentViewer'

export interface AttachmentPanelProps {
  ownerType: OwnerType
  ownerId: string
  /** Offer the "make this the picture" action. */
  allowPreview?: boolean
  /** Extra query keys to refresh after a change (the owner's own detail). */
  invalidate?: readonly (readonly unknown[])[]
}

export function AttachmentPanel({
  ownerType,
  ownerId,
  allowPreview = true,
  invalidate = [],
}: AttachmentPanelProps) {
  const queryClient = useQueryClient()
  const attachments = useAttachments(ownerType, ownerId)
  const types = useQuery({ queryKey: attachmentTypesKey, queryFn: fetchAttachmentTypes })

  const [dragging, setDragging] = useState(false)
  const [linking, setLinking] = useState(false)
  const [renaming, setRenaming] = useState<AttachmentView | null>(null)
  const [deleting, setDeleting] = useState<AttachmentView | null>(null)
  const [viewing, setViewing] = useState<AttachmentView | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const defaultTypeId = (types.data ?? []).find((type) => type.name === 'Datasheet')?.id

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: attachmentsKey(ownerType, ownerId) })
    for (const key of invalidate) {
      await queryClient.invalidateQueries({ queryKey: key })
    }
  }

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      if (!defaultTypeId) throw new Error('No attachment types are set up')
      for (const file of files) {
        await uploadAttachment({
          ownerType,
          ownerId,
          attachmentTypeId: guessTypeId(file, types.data ?? [], defaultTypeId),
          name: file.name,
          file,
        })
      }
    },
    onSuccess: async () => {
      toast.success('Uploaded')
      await refresh()
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Upload failed'),
  })

  const setPreview = useMutation({
    mutationFn: (attachment: AttachmentView) =>
      patchAttachment(attachment.id, { is_preview: !attachment.is_preview }),
    onSuccess: refresh,
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not save'),
  })

  const remove = useMutation({
    mutationFn: (attachment: AttachmentView) => deleteAttachment(attachment.id),
    onSuccess: async () => {
      setDeleting(null)
      toast.success('Removed')
      await refresh()
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not remove'),
  })

  const items = attachments.data ?? []

  return (
    <div className="flex flex-col gap-4">
      <div
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          if (event.dataTransfer.files.length > 0) {
            upload.mutate(Array.from(event.dataTransfer.files))
          }
        }}
        className={cn(
          'flex flex-col items-center gap-3 rounded-lg border border-dashed p-6 text-center transition-colors',
          dragging && 'border-primary bg-accent/40',
        )}
      >
        <p className="text-muted-foreground text-sm">Drop files here, or add one another way.</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? <Spinner /> : <UploadIcon />}
            Choose a file
          </Button>
          <Button type="button" variant="outline" onClick={() => setLinking(true)}>
            <LinkIcon />
            Add a link
          </Button>
        </div>
        <input
          ref={fileInput}
          type="file"
          multiple
          className="sr-only"
          aria-label="Choose a file"
          onChange={(event) => {
            // Snapshot the files before resetting the input: clearing `value`
            // empties the live FileList, and the upload runs asynchronously —
            // it would find nothing left to send.
            const chosen = Array.from(event.target.files ?? [])
            event.target.value = ''
            if (chosen.length > 0) upload.mutate(chosen)
          }}
        />
      </div>

      {attachments.isPending ? (
        <LoadingRows label="Loading attachments" rows={3} />
      ) : attachments.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load the attachments</AlertTitle>
          <AlertDescription>
            {attachments.error instanceof Error ? attachments.error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing attached yet.</p>
      ) : (
        <ul className="divide-border divide-y rounded-lg border">
          {items.map((attachment) => (
            <li key={attachment.id} className="flex items-center gap-3 p-3">
              <button
                type="button"
                onClick={() => setViewing(attachment)}
                className="hover:bg-accent grid size-12 shrink-0 place-items-center overflow-hidden rounded"
                aria-label={`Open ${attachment.name}`}
              >
                {isImage(attachment) && attachment.blob_sha256 ? (
                  <img
                    src={thumbUrl(attachment.id)}
                    alt=""
                    className="size-12 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <AttachmentIcon attachment={attachment} />
                )}
              </button>

              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 truncate text-sm font-medium">
                  {attachment.name}
                  {attachment.is_preview && (
                    <Badge variant="muted" className="shrink-0">
                      picture
                    </Badge>
                  )}
                </p>
                <p className="text-muted-foreground flex flex-wrap gap-x-2 text-xs">
                  <span>{attachment.attachment_type_name}</span>
                  {attachment.external_url ? (
                    <span>link</span>
                  ) : (
                    <>
                      <span>{attachment.mime ?? 'unknown type'}</span>
                      <span>{formatBytes(attachment.size_bytes)}</span>
                    </>
                  )}
                </p>
              </div>

              {allowPreview && isImage(attachment) && attachment.blob_sha256 && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={
                    attachment.is_preview
                      ? `Stop using ${attachment.name} as the picture`
                      : `Use ${attachment.name} as the picture`
                  }
                  onClick={() => setPreview.mutate(attachment)}
                >
                  <StarIcon
                    className={attachment.is_preview ? 'fill-warning text-warning' : undefined}
                  />
                </Button>
              )}
              <Button
                asChild
                variant="ghost"
                size="icon-sm"
                aria-label={`Download ${attachment.name}`}
              >
                <a href={fileUrl(attachment)} target="_blank" rel="noreferrer noopener">
                  <DownloadIcon />
                </a>
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Rename ${attachment.name}`}
                onClick={() => setRenaming(attachment)}
              >
                <PencilIcon />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove ${attachment.name}`}
                onClick={() => setDeleting(attachment)}
              >
                <Trash2Icon />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <LinkDialog
        open={linking}
        onOpenChange={setLinking}
        ownerType={ownerType}
        ownerId={ownerId}
        types={types.data ?? []}
        defaultTypeId={defaultTypeId}
        onDone={refresh}
      />

      <RenameDialog
        attachment={renaming}
        onClose={() => setRenaming(null)}
        types={types.data ?? []}
        onDone={refresh}
      />

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove “{deleting?.name}”?</DialogTitle>
            <DialogDescription>
              {deleting?.external_url
                ? 'The link is removed; whatever it points at is untouched.'
                : 'The file is removed from this entry.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              {remove.isPending && <Spinner />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AttachmentViewer attachment={viewing} onClose={() => setViewing(null)} />
    </div>
  )
}

function AttachmentIcon({ attachment }: { attachment: AttachmentView }) {
  if (attachment.external_url) return <LinkIcon className="text-muted-foreground size-5" />
  if (isPdf(attachment)) return <FileTextIcon className="text-muted-foreground size-5" />
  if (isImage(attachment)) return <ImageIcon className="text-muted-foreground size-5" />
  return <FileIcon className="text-muted-foreground size-5" />
}

function LinkDialog({
  open,
  onOpenChange,
  ownerType,
  ownerId,
  types,
  defaultTypeId,
  onDone,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  ownerType: OwnerType
  ownerId: string
  types: { id: string; name: string }[]
  defaultTypeId?: string
  onDone: () => Promise<void>
}) {
  const [url, setUrl] = useState('')
  const [name, setName] = useState('')
  const [download, setDownload] = useState(true)
  const [typeId, setTypeId] = useState<string | undefined>(defaultTypeId)
  const [error, setError] = useState<string | null>(null)

  const chosen = typeId ?? defaultTypeId

  const submit = useMutation({
    mutationFn: () =>
      importAttachmentUrl({
        ownerType,
        ownerId,
        attachmentTypeId: chosen as string,
        url: url.trim(),
        name: name.trim() || undefined,
        download,
      }),
    onSuccess: async () => {
      onOpenChange(false)
      setUrl('')
      setName('')
      toast.success(download ? 'Downloaded' : 'Link added')
      await onDone()
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'Could not add that link'),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a link</DialogTitle>
          <DialogDescription>Keep a copy, or just remember where it is.</DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            submit.mutate()
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="attachment-url">URL</Label>
            <Input
              id="attachment-url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://…"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="attachment-link-name">Name</Label>
            <Input
              id="attachment-link-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Taken from the URL if left empty"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="attachment-link-type">Type</Label>
            <Select value={chosen} onValueChange={setTypeId}>
              <SelectTrigger id="attachment-link-type" aria-label="Type">
                <SelectValue placeholder="Choose…" />
              </SelectTrigger>
              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={download}
              onCheckedChange={(checked) => setDownload(checked === true)}
              className="mt-0.5"
            />
            <span>
              Keep a copy
              <span className="text-muted-foreground block text-xs">
                Without it, the file stays wherever it is — which is fine until that page moves.
              </span>
            </span>
          </label>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submit.isPending || !url.trim() || !chosen}>
              {submit.isPending && <Spinner />}
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/// Renaming is a separate inner component keyed by the attachment, so its state
/// starts from that attachment rather than falling back to it on every render —
/// which made the name field impossible to clear.
function RenameDialog({
  attachment,
  onClose,
  types,
  onDone,
}: {
  attachment: AttachmentView | null
  onClose: () => void
  types: { id: string; name: string }[]
  onDone: () => Promise<void>
}) {
  return (
    <Dialog open={attachment !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        {attachment && (
          <RenameForm
            key={attachment.id}
            attachment={attachment}
            types={types}
            onClose={onClose}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function RenameForm({
  attachment,
  types,
  onClose,
  onDone,
}: {
  attachment: AttachmentView
  types: { id: string; name: string }[]
  onClose: () => void
  onDone: () => Promise<void>
}) {
  const [name, setName] = useState(attachment.name)
  const [typeId, setTypeId] = useState(attachment.attachment_type_id)

  const save = useMutation({
    mutationFn: () =>
      patchAttachment(attachment.id, { name: name.trim(), attachment_type_id: typeId }),
    onSuccess: async () => {
      onClose()
      toast.success('Saved')
      await onDone()
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not save'),
  })

  return (
    <>
      <DialogHeader>
        <DialogTitle>Rename</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-2">
        <Label htmlFor="attachment-name">Name</Label>
        <Input
          id="attachment-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="attachment-type">Type</Label>
        <Select value={typeId} onValueChange={setTypeId}>
          <SelectTrigger id="attachment-type" aria-label="Type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {types.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={save.isPending || !name.trim()} onClick={() => save.mutate()}>
          {save.isPending && <Spinner />}
          Save
        </Button>
      </DialogFooter>
    </>
  )
}
